import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ArchetypeId, ColorEmphasis, ColorMode } from '@mg/shared';
import { api } from '../../lib/api';
import { useDebounced } from '../../lib/useDebounced';

export interface PreviewInput {
  archetype: ArchetypeId | null;
  width: number;
  height: number;
  emphasis: ColorEmphasis;
  mode: ColorMode;
  headline?: string;
  subhead?: string;
  hasImage: boolean;
  variantSeed: number;
}

/**
 * The design as it will be, rendered as a blob URL an `<img>` can show.
 *
 * Two things make this workable rather than expensive. The server route costs a layout pass and a
 * string build — no model, no image, no write — so calling it on every change is fine. And react-query
 * keys on the whole input, so flipping between two sizes serves the second one from cache instead of
 * re-rendering: the "cache by template × size" the brief asks for falls out of the query key.
 *
 * The headline is DEBOUNCED and the rest is not, deliberately. Changing size or template is a discrete
 * decision and should redraw at once; typing is continuous, and re-rendering per keystroke would make
 * the thumbnail flicker while someone is mid-sentence.
 *
 * Rendered through an `<img>` rather than inline markup: the SVG comes from our own server, but so did
 * the template SVGs, and putting untrusted-shaped markup into the page is a habit worth not having.
 */
export function useDesignPreview(input: PreviewInput): {
  url: string | null;
  notes: string[];
  loading: boolean;
} {
  const headline = useDebounced(input.headline ?? '', 350);
  const subhead = useDebounced(input.subhead ?? '', 350);

  const query = useQuery({
    queryKey: [
      'design-preview',
      input.archetype,
      input.width,
      input.height,
      input.emphasis,
      input.mode,
      headline,
      subhead,
      input.hasImage,
      input.variantSeed,
    ],
    enabled: input.archetype != null && input.width > 0 && input.height > 0,
    queryFn: ({ signal }) =>
      api.previewDesign(
        {
          archetype: input.archetype as string,
          width: input.width,
          height: input.height,
          colorEmphasis: input.emphasis,
          colorMode: input.mode,
          text: headline || undefined,
          subtext: subhead || undefined,
          hasImage: input.hasImage,
          variantSeed: input.variantSeed,
        },
        signal,
      ),
    // A preview for a given input never changes, so once rendered it is good for the session.
    staleTime: Infinity,
    // Keep the previous image on screen while the next renders. Without this the thumbnail blanks on
    // every size change, which reads as breakage rather than as work in progress.
    placeholderData: (previous) => previous,
  });

  const url = useObjectUrl(query.data?.svg ?? null);
  return { url, notes: query.data?.notes ?? [], loading: query.isFetching };
}

/** A blob URL over SVG markup, revoked whenever the markup changes or the component unmounts. */
function useObjectUrl(svg: string | null): string | null {
  const blob = useMemo(() => (svg ? new Blob([svg], { type: 'image/svg+xml' }) : null), [svg]);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const created = URL.createObjectURL(blob);
    setUrl(created);
    // Revoked on change: a modal that stays open through twenty size changes would otherwise pin
    // twenty SVGs in memory for the life of the document.
    return () => {
      URL.revokeObjectURL(created);
    };
  }, [blob]);
  return url;
}
